import queryString from "query-string";

import Service from "shared/payment/services";
import { getStore } from "shared/redux/addToGlobalStore";
import {
	CREDIT_SCREEN,
	EXISTING_PAYMENT_SCREEN,
	PAYPAL_SCREEN,
	CREDIT_FORM_NAMES,
	PRODUCT_TYPE_MAP,
	PRODUCT_SUBTYPES,
	PRODUCT_DETAILS,
	CREDIT_CARD_PAYMENT_PROCESSOR,
	PAYPAL_PAYMENT_PROCESSOR,
	SAVE_PAYPAL_INFO_PRODUCT_ID,
} from "shared/payment/constants";
import { isMobile } from "shared/util/EnvironmentHelpers";
import {
	setBoostTokenCount,
} from "ducks/navbar";
import { purchasedStack } from "ducks/quickmatch";

import {
	OKEVENT,
	triggerOkEvent,
} from "shared/util/OkEventHelpers";

import getApiError from "shared/payment/helpers/getApiError";
import getApiErrorType from "shared/payment/helpers/getApiErrorType";

import {
	formatValue,
} from "shared/payment/helpers/format-value";

import {
	validateFormValue,
} from "shared/payment/helpers/form-validate";

import {
	getHumanReadableErrorMessages,
} from "shared/payment/helpers/form-errors";

import {
	getCreditCardTypeFromNumber,
} from "shared/payment/helpers/credit-card";

import getSelectedProduct from "shared/payment/helpers/getSelectedProduct";

import pickPaymentToken from "shared/payment/helpers/pick-payment-token";

import _str from "shared/util/_str";
import isApp from "shared/util/isApp";
import ClientStats from "shared/util/ClientStats";
import UrlHelpers from "shared/util/UrlHelpers";
import Context from "shared/util/Context";
import AppPhoneCommand from "shared/util/AppPhoneCommand";

const {
	cardholderName,
	cardNumber,
	cvv,
	expiration,
	zipCode,

	creditPurchaseScreen,
	paypalPurchaseScreen,
} = CREDIT_FORM_NAMES;


// initial state
// ----------------------------------------------------------------------------

export const INITIAL_STATE = {
	currentScreen: CREDIT_SCREEN,
	isEditingInfo: false, // this refers to editing billing information
	isVisible: false,
	product: null,
	productChoices: [],
	cardType: null,
	hasError: false,
	form: {
		[cardholderName]: "",
		[cardNumber]: "",
		[cvv]: "",
		[expiration]: "",
		[zipCode]: "",
	},
	errorMessage: {
		[cardholderName]: "",
		[cardNumber]: "",
		[cvv]: "",
		[expiration]: "",
		[zipCode]: "",
		[creditPurchaseScreen]: "",
		[paypalPurchaseScreen]: "",
	},
	paymentToken: {},
	userInfo: {
		state: "",
	},
	paypalLink: "",
	isLoading: true,
	isSuccessful: false,
	isPurchasing: false,
	needsUserState: true,
	discountId: "",
	rateCardName: "",
	isSettingPayPalLink: false,
};


// action types
// ----------------------------------------------------------------------------

export const TOGGLE_VISIBLE = "payment.TOGGLE_VISIBLE";
export const CLEAR_ERRORS = "payment.CLEAR_ERRORS";
export const SET_EDITING_INFO = "payment.SET_EDITING_INFO";
export const SET_PRODUCT = "payment.SET_PRODUCT";
export const SET_PRODUCTS = "payment.SET_PRODUCTS";
export const SET_CURRENT_SCREEN = "payment.SET_CURRENT_SCREEN";
export const SET_FORM = "payment.SET_FORM";
export const SET_CARD_TYPE = "payment.SET_CARD_TYPE";
export const SET_STATUS = "payment.SET_STATUS";
export const SET_ERROR_MESSAGE = "payment.SET_ERROR_MESSAGE";
export const SET_USER_STATE = "payment.SET_USER_STATE";
export const SET_USER_STATE_FAILED = "payment.SET_USER_STATE_FAILED";
export const SET_PAYPAL_LINK = "payment.SET_PAYPAL_LINK";
export const SETTING_PAYPAL_LINK = "payment.SETTING_PAYPAL_LINK";
export const SET_DISCOUNT_ID = "payment.SET_DISCOUNT_ID";
export const PURCHASE_STARTED = "payment.PURCHASE_STARTED";
export const PURCHASE_STOPPED = "payment.PURCHASE_STOPPED";
export const SUCCESS = "payment.SUCCESS";
export const ERROR = "payment.ERROR";

// reducer
// ----------------------------------------------------------------------------

export default (state = INITIAL_STATE, action) => {
	switch (action.type) {
		case CLEAR_ERRORS: {
			return {
				...state,
				errorMessage: {
					...INITIAL_STATE.errorMessage,
				},
			};
		}

		case SET_CURRENT_SCREEN: {
			return {
				...state,
				currentScreen: action.payload.currentScreen,
			};
		}

		case SUCCESS: {
			return {
				...state,
				hasError: false,
				isSuccessful: true,
			};
		}

		case PURCHASE_STARTED: {
			return {
				...state,
				isPurchasing: true,
				hasError: false,
			};
		}

		case PURCHASE_STOPPED: {
			return {
				...state,
				isPurchasing: false,
			};
		}

		case ERROR: {
			return {
				...state,
				hasError: true,
			};
		}

		case SET_PRODUCT: {
			return {
				...state,
				product: action.payload.product,
				isLoading: false,
				isEditingInfo: false,
			};
		}

		case SET_PRODUCTS: {
			return {
				...state,
				productChoices: action.payload.products,
				rateCardName: action.payload.rateCardName,
			};
		}

		case SET_EDITING_INFO: {
			return {
				...state,
				isEditingInfo: true,
				isLoading: false,
			};
		}

		case TOGGLE_VISIBLE: {
			return {
				...state,
				isVisible: action.payload.isVisible,
				isSuccessful: false,
			};
		}

		case SET_FORM: {
			return {
				...state,
				form: {
					...state.form,
					[action.payload.name]: action.payload.value,
				},
			};
		}

		case SET_CARD_TYPE: {
			return {
				...state,
				cardType: action.payload.cardType,
			};
		}

		case SET_STATUS: {
			return {
				...state,
				status: {
					...state.status,
					[action.payload.name]: action.payload.status,
				},
			};
		}

		case SET_ERROR_MESSAGE: {
			return {
				...state,
				errorMessage: {
					...state.errorMessage,
					[action.payload.name]: action.payload.errorMessage,
				},
			};
		}

		case SET_USER_STATE: {
			return {
				...state,
				needsUserState: false,
				paymentToken: action.payload.paymentToken,
				userInfo: action.payload.userInfo,
			};
		}

		case SET_USER_STATE_FAILED: {
			return {
				...state,
				needsUserState: false,
			};
		}

		case SET_PAYPAL_LINK: {
			return {
				...state,
				paypalLink: action.payload.paypalLink,
			};
		}

		case SETTING_PAYPAL_LINK: {
			return {
				...state,
				isSettingPayPalLink: action.payload.isSettingPayPalLink,
			};
		}

		case SET_DISCOUNT_ID: {
			return {
				...state,
				discountId: action.payload.discountId,
			};
		}

		default: {
			return state;
		}
	}
};

// actions
// ----------------------------------------------------------------------------


// this should only be used for programmatic screen-setting!
// like when an action wants to put you on a certian page!!!
// (that's why this isn't exported!!!!!!!!!!!!!!!!!!!!!!!!)
// if you want an action for the user, use navigateScreen
const setCurrentScreen = (currentScreen) => ({
	type: SET_CURRENT_SCREEN,
	payload: {
		currentScreen,
	},
});

export const setProduct = (product) => ({
	type: SET_PRODUCT,
	payload: {
		product,
	},
});

export const setErrorMessage = ({ name, errorMessage }) => (dispatch) => {
	dispatch({
		type: SET_ERROR_MESSAGE,
		payload: {
			name,
			errorMessage,
		},
	});

	// if this is a new error, always go back to the main page
	if (errorMessage !== "") {
		if (name === paypalPurchaseScreen) {
			dispatch(setCurrentScreen(PAYPAL_SCREEN));
		} else {
			dispatch(setCurrentScreen(CREDIT_SCREEN));
		}
	}
};

export const navigateScreen = (newScreen) => (dispatch) => {
	dispatch({ type: CLEAR_ERRORS });

	// navigate
	dispatch(setCurrentScreen(newScreen));
};

export const setCardType = (cardType) => ({
	type: SET_CARD_TYPE,
	payload: {
		cardType,
	},
});

export const setUserState = (payload) => ({
	type: SET_USER_STATE,
	payload,
});

export const handleFormBlur = ({ name }) => (dispatch, getState) => {
	const {
		form,
	} = getState().payment;

	const errors = validateFormValue({
		name,
		value: form[name],
	});

	const errorMessage = getHumanReadableErrorMessages({
		name,
		errors,
	}).toString();

	dispatch(setErrorMessage({
		name,
		errorMessage,
	}));
};

export const setForm = ({ name, value: newValue }) => (dispatch, getState) => {
	const {
		form,
		currentScreen,
		errorMessage,
	} = getState().payment;

	// clear out the form error too
	if (currentScreen === CREDIT_SCREEN && errorMessage[CREDIT_FORM_NAMES.creditPurchaseScreen]) {
		dispatch(setErrorMessage({
			name: CREDIT_FORM_NAMES.creditPurchaseScreen,
			errorMessage: "",
		}));
	}

	const oldValue = form[name];

	const value = formatValue({
		name,
		newValue,
		oldValue,
	});

	if (name === cardNumber) {
		const cardType = getCreditCardTypeFromNumber(value);

		dispatch(setCardType(cardType));
	}

	dispatch(setErrorMessage({
		name,
		errorMessage: "",
	}));

	dispatch({
		type: SET_FORM,
		payload: {
			name,
			value,
		},
	});
};

export const toggleVisible = (isVisible) => (dispatch, getState) => {
	const {
		product,
	} = getState().payment;


	if (process.env.PLATFORM === "desktop") {
		const noScrollElement = window.document.body;

		if (isVisible && !/noscroll/.test(noScrollElement.className)) {
			noScrollElement.className = `${noScrollElement.className} noscroll`;
		} else {
			noScrollElement.className = noScrollElement.className.replace(/\s*noscroll\s*/, "");
		}
	}

	dispatch({
		type: TOGGLE_VISIBLE,
		payload: {
			isVisible,
			product,
		},
	});
};

export const setProducts = (products, rateCardName) => ({
	type: SET_PRODUCTS,
	payload: { products, rateCardName },
});

export const setDiscountId = (discountId) => ({
	type: SET_DISCOUNT_ID,
	payload: { discountId },
});

export const open = (product) => (dispatch) => {
	dispatch(setProduct(product));
	dispatch(toggleVisible(true));
};

export const openCreditCard = (product) => (dispatch, getState) => {
	const { paymentToken } = getState().payment;

	dispatch(open(product));

	// if they already have stored credit card info, show the existing payment screen
	if (paymentToken && paymentToken.paymentProcessor === "USAEPAY") {
		dispatch(setCurrentScreen(EXISTING_PAYMENT_SCREEN));
	} else {
		dispatch(setCurrentScreen(CREDIT_SCREEN));
	}
};

export const openEditor = () => (dispatch) => {
	dispatch({ type: SET_EDITING_INFO });
	dispatch(setCurrentScreen(CREDIT_SCREEN));
	dispatch(toggleVisible(true));
};

export const openPaypalEditor = () => (dispatch) => {
	dispatch({ type: SET_EDITING_INFO });
	dispatch(setCurrentScreen(PAYPAL_SCREEN));
	dispatch(toggleVisible(true));
};

export const close = () => {
	return toggleVisible(false);
};

export const sendPurchaseSuccess = ({ product, purchasedProductids, method }) => {
	return (dispatch, getState) => {
		const txid = (purchasedProductids || [])
			.reduce((memo, purchase) => purchase.trackingId || memo, null);

		dispatch({ type: PURCHASE_STOPPED });
		dispatch({
			type: SUCCESS,
			payload: {
				...product,
				txid,
				method,
			},
		});

		// handle ALL success shit!!!!!!!!!!
		if (getState().payment.isEditingInfo) {
			window.location.reload();
		}

		else if (product.productSubtype === PRODUCT_SUBTYPES.BOOST) {
			if (process.env.PLATFORM === "desktop") {
				// update the boost count in the navbar
				// subtract 1 from the tokens purchased, since a boost token
				// is immediately used after purchase.
				const tokensRemaining = product.tokensNumber - 1;
				getStore().dispatch(setBoostTokenCount(tokensRemaining));
				window.BoostController.validateNewPurchase(product.tokensNumber);
			} else if (window.APP_PLATFORM && window.APP_PLATFORM === "android") {
				if (Context.has("ANDROID_BOOST_COUNTDOWN")) {
					AppPhoneCommand("purchasedBoostSuccess", {
						tokenCount: product.tokensNumber,
					});
					AppPhoneCommand("popActivity");
				} else {
					window.App.loadPage("/spotlight", { clear_history: 1 });
				}
			} else {
				window.location.href = "/startboost";
			}
		}

		else if (product.productSubtype === PRODUCT_SUBTYPES.INCOGNITO) {
			// check if there's an alt destination
			if (document.getElementById("incognito-destination")) {
				window.location.href = "/home";
			} else {
				// otherwise, reload without the hash
				window.location.href = window.location.href.replace(window.location.hash, "");
			}
		}

		else if (
			product.productSubtype === PRODUCT_SUBTYPES.ALIST ||
			product.productSubtype === PRODUCT_SUBTYPES.ALISTPREMIUM
		) {
			if (!isApp() && UrlHelpers.getParam("cf") === "matchcarouseltile") {
				window.location.href = "/who-likes-you";
			} else if (process.env.PLATFORM === "desktop") {
				window.location.href = window.location.href.replace(window.location.search, "");
			} else if (window.APP_PLATFORM && window.APP_PLATFORM === "android") {
				// show shadowbox or redirect them to the subscription page
				if (UrlHelpers.getParam("nativeRateCard") === "1") {
					window.App.phoneCommand("alistPurchaseCompleted");
				} else if (window.App.shadowbox && window.ShadowboxContent) {
					window.App.shadowbox.set(new window.ShadowboxContent({
						icon: "Congratulations_Shadowbox",
						title: "Success!",
						desc: _str("Thanks for becoming a member. You’re helping us make OkCupid great."),
						lock: true,
						actions: [{
							text: _str("Okay"),
							"save": true,
							js: "App.shadowbox.hide();App.profileBasicsChanged();App.loadPage('/who-likes-you', {clear_history: 1, request_refresh: 1});",
						}],
					})).show();
				} else {
					window.App.loadPage("/settings?page=subscriptions", { clear_history: 1 });
				}
			} else {
				// mw
				window.location.href = "/upgrade?success=1";
			}
		}

		else if (product.productSubtype === PRODUCT_SUBTYPES.READ_RECEIPT) {
			if (!isApp() && isMobile) {
				const { correspondentId } = queryString.parse(window.location.search);
				window.location.href = `/messages?readmsg=true&userid=${correspondentId}`;
			} else {
				triggerOkEvent(OKEVENT.ReadReceiptTokensPurchased);
			}
		}

		else if (product.productSubtype === PRODUCT_SUBTYPES.STACK_PASS) {
			if (!isApp() && isMobile) {
				const { stack } = queryString.parse(window.location.search);
				window.location.href = `/stacks?stack=${stack}&purchased_success=true`;
			} else {
				getStore().dispatch(purchasedStack(product.activeStackId));
			}
		}

		// close this shit in a little bit
		setTimeout(() => {
			dispatch(close());
		}, 2000);
	};
};

export function startPurchase(payload) {
	return { type: PURCHASE_STARTED, payload };
}

export const confirmPurchaseNewCard = () => (dispatch, getState) => {
	const {
		errorMessage,
		form,
		product,
		isPurchasing,
		isEditingInfo,
		paymentToken,
		discountId,
	} = getState().payment;

	if (isPurchasing) {
		return;
	}

	const isAllEmpty = Object.keys(form)
		.reduce((acc, key) => {
			if (form[key].length > 0 && acc) {
				return false;
			}

			return acc;
		}, true);

	const hasErrors = Object.keys(errorMessage)
		.reduce((acc, key) => {
			if (errorMessage[key].length > 0 || acc) {
				return true;
			}

			return acc;
		}, false);

	if (hasErrors || isAllEmpty) {
		return;
	}

	dispatch(startPurchase({
		product,
		method: CREDIT_CARD_PAYMENT_PROCESSOR,
		existingInfo: false,
	}));

	let action;

	if (isEditingInfo) {
		action = Service.updateCard({
			paymentToken,
			...form,
		});
	} else {
		action = Service.purchaseWithNewCard({
			productId: product.productid,
			discountId,
			...form,
		});
	}

	action
		.then(({ purchasedProductids, status, message }) => {
			if (!!purchasedProductids || status === 1) {
				dispatch(sendPurchaseSuccess({
					product,
					purchasedProductids: purchasedProductids || [],
					method: CREDIT_CARD_PAYMENT_PROCESSOR,
					existingInfo: false,
				}));
			} else {
				const { text } = getApiError(status);
				dispatch({ type: PURCHASE_STOPPED });
				dispatch(setErrorMessage({
					errorMessage: text,
					name: creditPurchaseScreen,
				}));

				dispatch({
					type: ERROR,
					payload: {
						product,
						method: CREDIT_CARD_PAYMENT_PROCESSOR,
						errorMessage: message,
						errorType: getApiErrorType(status),
					},
				});
			}
		})
		.catch((e) => {
			const { text } = getApiError(e.error.data.status);
			dispatch({ type: PURCHASE_STOPPED });
			dispatch(setErrorMessage({
				errorMessage: text,
				name: creditPurchaseScreen,
			}));

			dispatch({
				type: ERROR,
				payload: {
					product,
					method: CREDIT_CARD_PAYMENT_PROCESSOR,
					errorMessage: e.error.message,
					errorType: getApiErrorType(e.error.data.status),
				},
			});
		});
};

export const confirmPurchaseStoredInfo = ({ tokenid, paymentProcessor }) => (
	(dispatch, getState) => {
		const {
			product,
			isPurchasing,
			discountId,
		} = getState().payment;

		if (isPurchasing) {
			return;
		}

		dispatch(startPurchase({
			product,
			method: paymentProcessor,
			existingInfo: true,
		}));

		Service.purchaseWithInfo({
			productId: product.productid,
			discountId,
			tokenid,
			paymentProcessor,
		})
			.then(({ purchasedProductids }) => {
				dispatch(sendPurchaseSuccess({
					product,
					purchasedProductids,
					method: paymentProcessor,
					existingInfo: true,
				}));
			})
			.catch((e) => {
				const { text } = getApiError(e.error.data.status);
				dispatch({ type: PURCHASE_STOPPED });
				dispatch(setErrorMessage({
					errorMessage: text,
					name: creditPurchaseScreen,
				}));

				dispatch({
					type: ERROR,
					payload: {
						product,
						method: paymentProcessor,
						errorMessage: e.error.message,
						errorType: getApiErrorType(e.error.data.status),
					},
				});
			});
	}
);

function paypalClearHistory(params) {
	// replaceState to fix the url
	if (!window.history) {
		return;
	}

	const {
		productid,
		paypalsuccess,
		type,
		PayerID,
		platform,
		token,
		...pageParams
	} = params;

	const { origin, pathname } = window.location;

	const url = `${origin}${pathname}?${queryString.stringify(pageParams)}`;

	window.history.replaceState(null, null, url);
}

export const savePaypalInfo = (params) => (dispatch) => {
	dispatch(setCurrentScreen(PAYPAL_SCREEN));

	Service.savePaypalInfo(params)
		.then((res) => {
			if (res.status) {
				// reload the page
				window.location.reload();
			} else {
				dispatch(openPaypalEditor());
				dispatch(setErrorMessage({
					errorMessage: _str("Oops, we couldn’t link your PayPal account!"),
					name: paypalPurchaseScreen,
				}));
			}
		})
		.catch((e) => {
			const { text } = getApiError(e.status);

			dispatch(openPaypalEditor());
			dispatch(setErrorMessage({
				errorMessage: text,
				name: paypalPurchaseScreen,
			}));
		});
};

export const confirmPurchasePaypal = (params) => (dispatch, getState) => {
	dispatch(setCurrentScreen(PAYPAL_SCREEN));

	// don't send any details here so analytics don't fire
	dispatch(startPurchase());

	Service.purchaseWithPaypal(params)
		.then(({ purchasedProductids }) => {
			const {
				product,
			} = getState().payment;

			dispatch(sendPurchaseSuccess({
				product,
				purchasedProductids,
				method: PAYPAL_PAYMENT_PROCESSOR,
				existingInfo: false,
			}));
			paypalClearHistory(params);
		})
		.catch((e) => {
			dispatch({ type: PURCHASE_STOPPED });

			const { text, escalate } = getApiError(e.error.data.status);

			let errorMessage;

			if (e.error.message === "Successfully created transaction" && !escalate) {
				errorMessage = _str("Your purchase already went through!");
				paypalClearHistory(params);
			} else {
				errorMessage = text;

				if (escalate) {
					errorMessage = `
						${errorMessage}
						${_str("Please email this token to customer support:")}
						${params.token}
					`;
				}
			}

			dispatch(setErrorMessage({
				errorMessage,
				name: paypalPurchaseScreen,
			}));

			dispatch({
				type: ERROR,
				payload: {
					product: getState().payment.product,
					method: PAYPAL_PAYMENT_PROCESSOR,
					errorMessage: e.error.message,
					errorType: getApiErrorType(e.error.data.status),
				},
			});
		});
};

export const fetchPaypalLink = () => (dispatch, getState) => {
	const { payment } = getState();

	// if there is no product set in state,
	// this means the user is trying to save their paypal payment info
	const productId = payment.product && payment.product.productid ?
		payment.product.productid : SAVE_PAYPAL_INFO_PRODUCT_ID;

	dispatch({
		type: SETTING_PAYPAL_LINK,
		payload: {
			isSettingPayPalLink: true,
		},
	});

	Service.getPaypalLink({
		...(window.location || {}),
		...payment,
		productId,
	})
		.then((res) => {
			dispatch({
				type: SET_PAYPAL_LINK,
				payload: {
					paypalLink: res.redirectUrl,
				},
			});

			dispatch({
				type: SETTING_PAYPAL_LINK,
				payload: {
					isSettingPayPalLink: false,
				},
			});
		})
		.catch(() => {
			dispatch(setErrorMessage({
				errorMessage: _str("Whoops, we couldn’t fetch a PayPal link!"),
				name: paypalPurchaseScreen,
			}));

			dispatch({
				type: SETTING_PAYPAL_LINK,
				payload: {
					isSettingPayPalLink: false,
				},
			});
		});
};

export const openPaypal = (product) => (dispatch, getState) => {
	const { paymentToken } = getState().payment;
	dispatch(open(product));
	dispatch(fetchPaypalLink());

	// if they already have stored credit card info, show the existing payment screen
	// else show the paypal screen with the button "connect to paypal"
	if (paymentToken && paymentToken.paymentProcessor === "PAYPAL") {
		dispatch(setCurrentScreen(EXISTING_PAYMENT_SCREEN));
	} else {
		dispatch(setCurrentScreen(PAYPAL_SCREEN));
	}
};


export const getUserState = () => (dispatch, getState) => {
	Service.getUserState()
		.then((res) => {
			const paymentToken = pickPaymentToken(res.paymentTokensList);
			const { isPurchasing, errorMessage, isEditingInfo, currentScreen } = getState().payment;

			dispatch(setUserState({
				paymentToken,
				userInfo: res.userinfo,
			}));

			// check for errors, since the paypal process coulda dundat
			const hasErrors = Object.keys(errorMessage)
				.reduce((acc, key) => {
					if (errorMessage[key].length > 0 || acc) {
						return true;
					}

					return acc;
				}, false);

			const hasPaypalError = errorMessage.paypalPurchaseScreen.length > 0;

			if (Object.keys(paymentToken).length && !isPurchasing && !hasErrors && !isEditingInfo) {
				if (hasPaypalError) {
					dispatch(setCurrentScreen(PAYPAL_SCREEN));
				} else if (
					(currentScreen === PAYPAL_SCREEN && paymentToken.paymentProcessor === "PAYPAL") ||
					(currentScreen === CREDIT_SCREEN && paymentToken.paymentProcessor === "USAEPAY")
				) {
					dispatch(setCurrentScreen(EXISTING_PAYMENT_SCREEN));
				}
			}
		})
		.catch(() => {
			dispatch({ type: SET_USER_STATE_FAILED });
		});
};

export const fetchProductId = ({
	productid,
	productSubtype,
	tokensNumber,
	subPeriod,
}) => (dispatch) => {
	const productType = PRODUCT_TYPE_MAP[productSubtype];

	Service.getProductPackages({
		productType,
		productSubtype,
	})
		.then(({ productPackages, rateCardName } = {}) => {
			// if there are no results, fuck off
			if (!productPackages || !productPackages.length) {
				const isOnApp = isApp();
				const platform = isOnApp ? "app" : process.env.PLATFORM;

				if (isOnApp) {
					window.App.loadPage("/doubletake", { clear_history: 1 });
				} else {
					window.location.href = "/doubletake";
				}

				ClientStats.update(`purchases - platform:${platform} - product subtype:${productSubtype} - fetch failed`);

				return;
			}

			// by default, fetch the last product
			let product = productPackages[productPackages.length - 1];

			// otherwise, pick the one we picked
			if (productid) {
				product = getSelectedProduct(
					productPackages,
					{ name: PRODUCT_DETAILS.ID, value: productid },
				);
			} else if (productSubtype === PRODUCT_SUBTYPES.BOOST && tokensNumber) {
				product = getSelectedProduct(
					productPackages,
					{ name: PRODUCT_DETAILS.TOKENS_NUM, value: tokensNumber },
				);
			} else if (
				(productSubtype === PRODUCT_SUBTYPES.ALIST ||
				productSubtype === PRODUCT_SUBTYPES.ALISTPREMIUM ||
				productSubtype === PRODUCT_SUBTYPES.INCOGNITO
				) &&
				subPeriod
			) {
				product = getSelectedProduct(
					productPackages,
					{ name: PRODUCT_DETAILS.SUB_PERIOD, value: subPeriod },
				);
			}

			dispatch(setProducts(productPackages, rateCardName));
			dispatch(open(product));
		})
		.catch(() => {
			dispatch({ type: SET_USER_STATE_FAILED });
		});
};

export const forceCardForm = () => (setCurrentScreen(CREDIT_SCREEN));



// WEBPACK FOOTER //
// ./src/ducks/payment.js